/**
 * @file: ChatFlow.tsx
 *
 * `ChatFlow` — the app-level (`_chat`) chat stack: a conversation plus the
 * pages reachable from it (room detail → add-members picker).
 *
 * It is pushed on the ROOT navigator like every other heavyweight sub-flow
 * (`Task`, `_order`, `_lead`, `_reservation`), NOT nested in the inbox tab: a
 * chat room is a full-screen surface. The composer pads itself to the SCREEN
 * bottom (safe area + keyboard height), so a bottom tab bar underneath would
 * double-count that inset — leaving a dead strip below the input bar, and a
 * whole tab bar's worth of it once the keyboard is up.
 *
 * Providers mirror `FloatingChatStack` — `ChatTransport` (the refcounted WS
 * hold, so the connection stays up while the flow is open) and
 * `ChatContactsProvider` (the add-members picker reads the contact store). NOT
 * `ChatThreadsProvider`: this flow has no thread list, and a second thread
 * reducer would double-count the shared unread badge — the conversation reads
 * the thread store optionally (it stays mounted in the inbox tab underneath).
 *
 * Its route params ARE the `ConversationIntent` to open; they seed
 * `_chatConversation`'s initial params. Open it via `useOpenChatConversation` /
 * `pushChatConversation`, never by navigating to `_chat` directly.
 */

import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { useStatusBarStyle } from '@euler/app/components/AppStatusBar';
import { ChatContactsProvider } from '@euler/app/flows/chat/lib/contacts/provider';
import { ConversationIntent } from '@euler/app/flows/chat/lib/conversation/types';
import { ChatTransport } from '@euler/app/flows/chat/lib/transport';
import { ChatScreens } from '@euler/app/flows/chat/screens';
import { useWhiteHeaderStackOptions } from '@euler/app/navigation/whiteHeaderStackOptions';
import { ErrorFallback } from '@euler/components/error';
import { spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { createStackNavigator } from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { memo } from 'react';

const Stack = createStackNavigator();

export const ChatFlow = wrapNavigatorScreen(
  (intent: ConversationIntent) => {
    useStatusBarStyle('dark');
    return <Content intent={intent} />;
  },
  {
    title: '聊天',
    headerShown: false,
  },
);

const Content = memo(({ intent }: { intent: ConversationIntent }) => {
  const screenOptions = useWhiteHeaderStackOptions();
  return (
    <AppNavigationProvider>
      <Sentry.ErrorBoundary
        // @ts-ignore
        fallback={(params: any) => <ErrorFallback {...params} />}
      >
        <ChatTransport>
          <ChatContactsProvider>
            <Stack.Navigator
              initialRouteName="_chatConversation"
              screenOptions={screenOptions}
            >
              {spreadScreens(
                Stack.Screen,
                ChatScreens,
                '_chatConversation',
                intent,
              )}
            </Stack.Navigator>
          </ChatContactsProvider>
        </ChatTransport>
      </Sentry.ErrorBoundary>
    </AppNavigationProvider>
  );
});
